/** THE CLIENT AREA — the single place MJ goes INTO a client. */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Chrome } from './V2AdminOSPage';
import { BG, TXT, SUB, TEAL, AMBER, MAGENTA, VIOLET, BORDER, HEAD, SPEC, HEAT, mini } from './V2DeskTiles';

const API = process.env.REACT_APP_BACKEND_URL;
const req = async (method, path, body) => {
  const r = await fetch(`${API}${path}`, {
    method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('kindra-admin-token') || ''}` },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const d = await r.json().catch(() => ({}));
  if (r.status === 401) { localStorage.removeItem('kindra-admin-token'); window.location.assign('/admin/login?returnTo=%2Fadmin%2Fclients'); throw new Error('auth'); }
  if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
  return d;
};

const card = { background: 'rgba(255,255,255,0.045)', border: `1px solid ${BORDER}`, borderRadius: 14, padding: '14px 16px' };
const label = { margin: '0 0 6px', fontFamily: 'Inter', fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: SUB };
const inputStyle = { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '8px 11px', fontFamily: 'Inter', fontSize: 12.5, color: TXT, outline: 'none' };
const chipS = (c) => ({ fontFamily: 'Inter', fontSize: 9.5, fontWeight: 800, color: c, border: `1px solid ${c}44`, borderRadius: 999, padding: '2px 9px' });
const fmtTs = (ts) => { try { return new Date(ts).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ts; } };

const Avatar = ({ title, heat, size = 46 }) => (
  <span style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', fontFamily: HEAD, fontWeight: 800, fontSize: size * 0.42, color: '#0B0526', background: SPEC, border: `2.5px solid ${HEAT[heat] || TEAL}`, boxShadow: heat === 'hot' ? `0 0 14px ${MAGENTA}55` : 'none' }}>
    {(title || '?').slice(0, 1).toUpperCase()}
  </span>
);

/* ── GRID ── */
const ClientsGrid = ({ nav }) => {
  const [clients, setClients] = useState(null);
  const [adding, setAdding] = useState('');
  const load = useCallback(() => req('GET', '/api/admin/desk/board').then((b) => setClients(b.clients || [])).catch(() => setClients([])), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    const t = adding.trim(); if (!t) return;
    setAdding('');
    await req('POST', '/api/admin/desk/items', { type: 'client', title: t });
    load();
  };
  return (
    <div data-testid="client-area-grid">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <h1 style={{ fontFamily: HEAD, fontSize: 24, fontWeight: 800, color: TXT, margin: 0, flex: 1 }}>Clients</h1>
        <input data-testid="client-new-input" placeholder="＋ New client — Enter" value={adding} onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && create()} style={{ ...inputStyle, width: 240 }} />
      </div>
      {!clients && <p style={{ fontFamily: 'Inter', color: SUB }}>Loading…</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
        {(clients || []).map((c) => (
          <button key={c.item_id} data-testid={`client-grid-card-${c.item_id}`} onClick={() => nav(`/admin/clients/${c.item_id}`)}
            style={{ ...card, display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', textAlign: 'left', transition: 'transform 150ms ease, border-color 150ms ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}>
            <Avatar title={c.title} heat={c.heat} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 13.5, fontWeight: 800, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.title}</p>
              <p style={{ margin: '3px 0 0', fontFamily: 'Inter', fontSize: 10.5, color: SUB, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.next || 'no next action'}</p>
              <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                <span style={chipS(HEAT[c.heat] || TEAL)}>{c.heat}</span>
                <span style={chipS(SUB)}>{c.state}</span>
              </div>
            </div>
            <span style={{ color: TEAL, fontFamily: 'Inter', fontSize: 14 }}>→</span>
          </button>
        ))}
      </div>
      {clients && clients.length === 0 && <p style={{ fontFamily: 'Inter', color: SUB }}>No clients yet — type a name above, Enter files it.</p>}
    </div>
  );
};

/* ── DETAIL ── */
const FIELDS = [['title', 'Name'], ['business_name', 'Business'], ['industry', 'Industry'], ['phone', 'Phone'], ['email', 'Email'], ['address', 'Address'], ['abn', 'ABN']];
const TABS = ['overview', 'timeline', 'notes', 'files', 'tasks'];
const KIND_ICON = { send: ['✉', TEAL], reply: ['↩', MAGENTA], note: ['📝', AMBER], done: ['✓', VIOLET] };

const ClientDetail = ({ itemId, nav }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('overview');
  const [edit, setEdit] = useState(null);
  const [noteVal, setNoteVal] = useState('');
  const [taskVal, setTaskVal] = useState('');
  const [fuDate, setFuDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => req('GET', `/api/admin/desk/clients/${itemId}`).then(setD).catch((e) => setErr(e.message)), [itemId]);
  useEffect(() => { load(); }, [load]);

  if (err) return <p style={{ fontFamily: 'Inter', color: MAGENTA }}>{err} — <button onClick={() => nav('/admin/clients')} style={{ ...mini, color: TEAL }}>← All clients</button></p>;
  if (!d) return <p style={{ fontFamily: 'Inter', color: SUB }}>Loading…</p>;
  const c = d.client;

  const saveEdit = async () => {
    await req('PATCH', `/api/admin/desk/items/${itemId}`, edit);
    setEdit(null); load();
  };
  const addNote = async () => {
    const t = noteVal.trim(); if (!t) return;
    setNoteVal('');
    await req('POST', `/api/admin/desk/clients/${itemId}/notes`, { text: t });
    load();
  };
  const addTask = async (due) => {
    const t = taskVal.trim(); if (!t) return;
    setTaskVal(''); setFuDate('');
    await req('POST', '/api/admin/desk/items', { type: 'task', title: t, client_ref: c.client_ref || null, client_tag: c.title, ...(due ? { due_date: due } : {}) });
    load();
  };
  const compose = () => {
    if (!d.lead?.lead_id) return;
    localStorage.setItem('desk-compose-lead', d.lead.lead_id);
    nav(`/admin/outreach?compose=1&lead=${d.lead.lead_id}`);
  };
  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', f);
    const r = await fetch(`${API}/api/admin/desk/clients/${itemId}/files`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('kindra-admin-token') || ''}` }, body: fd });
    setUploading(false);
    if (r.ok) { load(); } else { setErr('upload failed'); setTimeout(() => setErr(null), 3000); }
  };
  const download = async (f) => {
    const r = await fetch(`${API}/api/admin/desk/clients/${itemId}/files/${f.file_id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('kindra-admin-token') || ''}` } });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = f.name; a.click();
    URL.revokeObjectURL(a.href);
  };

  const notTracked = <span style={{ color: SUB, fontStyle: 'italic', fontSize: 11 }}>not tracked yet</span>;
  return (
    <div data-testid="client-detail">
      <button data-testid="client-back" onClick={() => nav('/admin/clients')} style={{ ...mini, marginBottom: 14, color: SUB }}>← All clients</button>

      {/* header */}
      <div style={{ ...card, display: 'flex', gap: 16, alignItems: 'center', marginBottom: 14 }}>
        <Avatar title={c.title} heat={c.heat} size={58} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontFamily: HEAD, fontSize: 24, fontWeight: 800, color: TXT, margin: 0 }}>{c.title}</h1>
          <p style={{ margin: '3px 0 0', fontFamily: 'Inter', fontSize: 11.5, color: SUB }}>
            {[c.business_name, c.industry, c.email, c.phone].filter(Boolean).join(' · ') || 'no details yet — hit Edit'}
          </p>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <span style={chipS(HEAT[c.heat] || TEAL)}>{c.heat} · {c.stale_days}d</span>
            <span style={chipS(SUB)}>{c.state}</span>
            {d.lead && <span style={chipS(VIOLET)}>linked lead</span>}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
          <button data-testid="client-edit-btn" onClick={() => setEdit(edit ? null : Object.fromEntries(FIELDS.map(([k]) => [k, c[k] || ''])))} style={{ ...mini, color: AMBER, borderColor: 'rgba(240,195,109,0.4)' }}>✎ Edit</button>
          {d.lead && <button data-testid="client-compose-btn" onClick={compose} style={{ ...mini, color: TEAL, borderColor: 'rgba(0,229,195,0.4)' }}>✉ Compose</button>}
        </div>
      </div>

      {/* edit form */}
      {edit && (
        <div data-testid="client-edit-form" style={{ ...card, marginBottom: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {FIELDS.map(([k, l]) => (
              <div key={k}>
                <p style={label}>{l}</p>
                <input data-testid={`client-edit-${k}`} value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} style={inputStyle} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button data-testid="client-edit-save" onClick={saveEdit} style={{ ...mini, color: '#0B0526', background: TEAL, border: 'none', fontWeight: 800 }}>Save</button>
            <button onClick={() => setEdit(null)} style={{ ...mini, color: SUB }}>Cancel</button>
          </div>
        </div>
      )}

      {/* tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button key={t} data-testid={`client-tab-${t}`} onClick={() => setTab(t)}
            style={{ ...mini, textTransform: 'capitalize', ...(tab === t ? { color: '#0B0526', background: TEAL, border: 'none', fontWeight: 800 } : {}) }}>{t}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
          <div style={card} data-testid="client-overview-money">
            <p style={label}>Money</p>
            {[['Quotes out', d.money.quotes_out > 0 ? `$${d.money.quotes_out.toLocaleString()}` : d.money.quotes.length ? '$0' : notTracked, TEAL],
              ['Invoiced', d.money.invoiced == null ? notTracked : `$${d.money.invoiced}`, AMBER],
              ['Paid', d.money.paid == null ? notTracked : `$${d.money.paid}`, VIOLET]].map(([l, v, cl]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span style={{ fontFamily: 'Inter', fontSize: 11.5, color: SUB }}>{l}</span>
                <b style={{ fontFamily: 'Inter', fontSize: 12.5, color: cl }}>{v}</b>
              </div>
            ))}
            {d.money.quotes.map((q) => (
              <p key={q.quote_id} style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 10.5, color: SUB }}>quote {q.quote_id?.slice(0, 6)} · {q.status} · ${Number(q.value).toLocaleString()}</p>
            ))}
          </div>
          <div style={card} data-testid="client-overview-next">
            <p style={label}>Next action</p>
            {d.tasks.filter((t) => t.state !== 'done').slice(0, 3).map((t) => (
              <p key={t.item_id} style={{ margin: '4px 0', fontFamily: 'Inter', fontSize: 12, color: TXT }}>⚡ {t.title}{t.due_date ? <span style={{ color: AMBER, fontSize: 10 }}> · {t.due_date.slice(5)}</span> : ''}</p>
            ))}
            {d.tasks.filter((t) => t.state !== 'done').length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: SUB, margin: 0 }}>Nothing queued — add a task below.</p>}
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <input data-testid="client-quick-task" placeholder="＋ Task — Enter" value={taskVal} onChange={(e) => setTaskVal(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTask(fuDate || null)} style={{ ...inputStyle, flex: 1 }} />
              <input data-testid="client-quick-task-date" type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)} title="book follow-up"
                style={{ ...inputStyle, width: 130, colorScheme: 'dark' }} />
            </div>
          </div>
          <div style={card} data-testid="client-overview-latest">
            <p style={label}>Latest activity</p>
            {d.timeline.slice(0, 4).map((t, k) => (
              <p key={k} style={{ margin: '4px 0', fontFamily: 'Inter', fontSize: 11.5, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ color: KIND_ICON[t.kind]?.[1] || SUB }}>{KIND_ICON[t.kind]?.[0] || '•'}</span> {t.title} <span style={{ color: SUB, fontSize: 9.5 }}>{fmtTs(t.ts)}</span>
              </p>
            ))}
            {d.timeline.length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: SUB, margin: 0 }}>Quiet so far.</p>}
          </div>
          <div style={card} data-testid="client-overview-projects">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
              <p style={{ ...label, margin: 0, flex: 1 }}>Projects</p>
              <a href="/admin/projects" data-testid="client-projects-open-all" style={{ fontFamily: 'Inter', fontSize: 10, color: TEAL, textDecoration: 'none', fontWeight: 700 }}>all →</a>
            </div>
            {(d.projects || []).length === 0 ? (
              <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: SUB, margin: '6px 0 0', lineHeight: 1.5 }}>
                No linked projects.<br/>
                <a href="/admin/projects" style={{ color: TEAL, textDecoration: 'none', fontWeight: 700 }}>+ Start a project →</a>
              </p>
            ) : (
              <>
                {(d.projects || []).slice(0, 5).map((p) => (
                  <a key={p.project_id} href={`/admin/projects`} data-testid={`client-project-${p.project_id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px', margin: '3px 0', borderRadius: 8, background: 'rgba(255,255,255,0.03)', textDecoration: 'none', color: TXT }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: { idea: SUB, active: TEAL, paused: AMBER, delivered: '#4F8FD0', maintained: VIOLET }[p.stage] || SUB, flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, fontFamily: 'Inter', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                    <span style={{ fontFamily: 'Inter', fontSize: 9.5, color: SUB, textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>{p.stage}</span>
                    {p.emergent_link_count > 0 && <span title="Emergent build links" style={{ color: TEAL, fontFamily: 'Inter', fontSize: 9.5, fontWeight: 800 }}>◈{p.emergent_link_count}</span>}
                  </a>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'timeline' && (
        <div style={card} data-testid="client-timeline">
          {d.timeline.map((t, k) => (
            <div key={k} style={{ display: 'flex', gap: 10, padding: '7px 4px', borderBottom: k < d.timeline.length - 1 ? `1px solid rgba(255,255,255,0.05)` : 'none' }}>
              <span style={{ width: 20, textAlign: 'center', color: KIND_ICON[t.kind]?.[1] || SUB, flexShrink: 0 }}>{KIND_ICON[t.kind]?.[0] || '•'}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 12.5, color: TXT }}>{t.title}</p>
                <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 10, color: SUB }}>{t.sub} · {fmtTs(t.ts)}</p>
              </div>
            </div>
          ))}
          {d.timeline.length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: SUB, margin: 0 }}>Nothing yet — sends, replies, notes and wins land here.</p>}
        </div>
      )}

      {tab === 'notes' && (
        <div style={card} data-testid="client-notes">
          <input data-testid="client-note-input" placeholder="📝 Log a note — Enter files it" value={noteVal} onChange={(e) => setNoteVal(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addNote()} style={{ ...inputStyle, marginBottom: 12 }} />
          {d.notes.map((n) => (
            <div key={n.note_id} style={{ padding: '8px 10px', margin: '6px 0', borderRadius: 10, background: 'rgba(255,255,255,0.035)', borderLeft: `2px solid ${n.source === 'ai' ? VIOLET : AMBER}` }}>
              <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 12.5, color: TXT, whiteSpace: 'pre-wrap' }}>{n.text}</p>
              <p style={{ margin: '3px 0 0', fontFamily: 'Inter', fontSize: 9.5, color: SUB }}>{n.source} · {fmtTs(n.created_at)}</p>
            </div>
          ))}
          {d.notes.length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: SUB, margin: 0 }}>No notes — append-only, so write freely.</p>}
        </div>
      )}

      {tab === 'files' && (
        <div style={card} data-testid="client-files">
          <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={(e) => upload(e.target.files?.[0])} />
          <button data-testid="client-file-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            style={{ ...mini, color: TEAL, borderColor: 'rgba(0,229,195,0.4)', marginBottom: 12 }}>{uploading ? 'Uploading…' : '⇪ Upload file'}</button>
          {d.files.map((f) => (
            <div key={f.file_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', margin: '4px 0', borderRadius: 10, background: 'rgba(255,255,255,0.035)' }}>
              <span>📎</span>
              <p style={{ margin: 0, flex: 1, minWidth: 0, fontFamily: 'Inter', fontSize: 12, color: TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</p>
              <span style={{ fontFamily: 'Inter', fontSize: 10, color: SUB }}>{Math.round((f.size || 0) / 1024)}KB</span>
              <button data-testid={`client-file-dl-${f.file_id}`} onClick={() => download(f)} style={{ ...mini, padding: '2px 9px', fontSize: 10, color: TEAL }}>↓</button>
            </div>
          ))}
          {d.files.length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: SUB, margin: 0 }}>No files attached yet.</p>}
        </div>
      )}

      {tab === 'tasks' && (
        <div style={card} data-testid="client-tasks">
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <input data-testid="client-task-input" placeholder="＋ Task for this client — Enter" value={taskVal} onChange={(e) => setTaskVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTask(fuDate || null)} style={{ ...inputStyle, flex: 1 }} />
            <input type="date" value={fuDate} onChange={(e) => setFuDate(e.target.value)} style={{ ...inputStyle, width: 130, colorScheme: 'dark' }} />
          </div>
          {d.tasks.map((t) => (
            <div key={t.item_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', margin: '3px 0', borderRadius: 9, background: 'rgba(255,255,255,0.03)' }}>
              <span style={{ color: t.state === 'done' ? TEAL : SUB }}>{t.state === 'done' ? '✓' : '○'}</span>
              <p style={{ margin: 0, flex: 1, minWidth: 0, fontFamily: 'Inter', fontSize: 12, color: TXT, textDecoration: t.state === 'done' ? 'line-through' : 'none', opacity: t.state === 'done' ? 0.55 : 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</p>
              {t.list_name && <span style={chipS(VIOLET)}>{t.list_name}</span>}
              {t.due_date && <span style={chipS(AMBER)}>📅 {t.due_date.slice(5)}</span>}
              <span style={chipS(SUB)}>{t.state}</span>
            </div>
          ))}
          {d.tasks.length === 0 && <p style={{ fontFamily: 'Inter', fontSize: 11.5, color: SUB, margin: 0 }}>Nothing tagged to this client yet.</p>}
        </div>
      )}
    </div>
  );
};

export default function V2ClientAreaPage() {
  const { itemId } = useParams();
  const nav = useNavigate();
  return (
    <Chrome activeTab="clients">
      {itemId ? <ClientDetail itemId={itemId} nav={nav} /> : <ClientsGrid nav={nav} />}
    </Chrome>
  );
}
